import React from "react";
import { Box, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { DateRangePicker, PickerType } from "halifax";
import { useSelector } from "react-redux";
import { IMonthBucket, Maybe } from "redmond";

import clsx from "clsx";
import { AVAILABLE } from "@capone/experiments";
import { MonthAndDatePickerType } from "../../constants";
import { getPriceLegend } from "../../selectors";
import "./styles.scss";
import {
  useExperiments,
  getExperimentVariant,
  ActiveExperiments,
} from "../../context/experiments";

export interface IMonthAndDatePickerProps {
  className?: string;
  endDate: Maybe<Date>;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  focusedMonthIndex?: number;
  header?: JSX.Element | string;
  months: IMonthBucket[];
  pickerType: PickerType;
  setEndDate: (date: Maybe<Date>) => void;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  setStartDate: (date: Maybe<Date>) => void;
  startDate: Maybe<Date>;
  viewType: MonthAndDatePickerType;
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    className,
    endDate,
    focusedMonthIndex,
    header,
    months,
    pickerType,
    setEndDate,
    setFocusedMonthIndex,
    setStartDate,
    startDate,
    viewType,
  } = props;
  const priceTags = useSelector(getPriceLegend);

  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, ActiveExperiments.XmodV1) ===
    AVAILABLE;

  return (
    // Intentionally give this Box a blank aria-label so parent can provide label
    <Box aria-label="">
      {header && (
        <Box
          aria-label="Header."
          className="month-and-date-picker-header-container"
        >
          <Typography variant="body2">{header}</Typography>
        </Box>
      )}
      <DateRangePicker
        className={clsx(className, { xmod: isXmodRedesign })}
        minAllowedDate={dayjs().toDate()}
        maxAllowedDate={dayjs().add(1, "year").toDate()}
        months={months}
        priceTags={priceTags}
        startDate={startDate}
        endDate={endDate}
        usePriceTagSymbols={isXmodRedesign}
        showDayPriceSymbols={isXmodRedesign}
        showOverPricingText={!isXmodRedesign}
        trimLegendValues={!isXmodRedesign}
        showFullMonthName={isXmodRedesign}
        {...(viewType === MonthAndDatePickerType.Horizontal ||
        viewType === MonthAndDatePickerType.Column
          ? { setStartDate, setEndDate, pickerType }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Column
          ? { focusedMonthIndex, columnView: true }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Group
          ? { setFocusedMonthIndex, groupView: true }
          : undefined)}
      />
    </Box>
  );
};
